/**
 * Centralised NZ tax constants for the Tradie Calculator.
 *
 * IRD updates these values every income year. When a new year lands,
 * bump the values here in ONE place — every calculator that uses this
 * module will pick up the change automatically.
 *
 * Current tax year: 2025–26 (1 April 2025 → 31 March 2026).
 */

export const TAX_YEAR = "2025-26";

// -------- Progressive PAYE brackets (individual, excludes ACC) ----------
// Effective 1 April 2025.
export const TAX_BRACKETS = [
  { cap: 15600, rate: 0.105 },
  { cap: 53500, rate: 0.175 },
  { cap: 78100, rate: 0.3 },
  { cap: 180000, rate: 0.33 },
  { cap: Infinity, rate: 0.39 },
];

// -------- ACC earner's levy (paid on earnings from work) ----------------
// 2025–26: 1.67% capped at $152,790 of earnings.
export const ACC_EARNER_LEVY = {
  rate: 0.0167,
  cap: 152790,
};

// -------- GST ----------------------------------------------------------
export const GST_RATE = 0.15;
// If your turnover is under this in a 12-month period you generally don't
// need to register for GST (IRD threshold, 2025–26).
export const GST_REGISTRATION_THRESHOLD = 60000;

// -------- IRD kilometre rates (2025–26, published 27 May 2025) ----------
// Tier 1: applied to the FIRST 14,000 km of total vehicle travel
//   (business + private) in the income year, pro-rated to business use.
// Tier 2: applied to all business kilometres beyond that threshold.
// Rates are per km.
export const IRD_KM_TIER_THRESHOLD = 14000;

export const IRD_KM_RATES = {
  petrol:        { tier1: 1.20, tier2: 0.37 },
  diesel:        { tier1: 1.30, tier2: 0.38 },
  petrol_hybrid: { tier1: 0.90, tier2: 0.24 },
  ev:            { tier1: 1.22, tier2: 0.23 },
};

// -------- Minimum wage (adult, gross) ----------------------------------
// 1 April 2025 → 31 March 2026 rate.
export const MIN_WAGE_HOURLY = 23.50;

// -------- Uber / gig GST flat-rate scheme -------------------------------
// From 1 April 2024 the marketplace operator (Uber) remits 6.5% GST to
// IRD on behalf of NON-GST-registered drivers and credits 8.5% back to
// the driver — a net 8.5% flat-rate credit. Displayed for context in the
// rideshare calc; no adjustment is needed because Uber's payout already
// reflects it.
export const GIG_FLAT_RATE = {
  ird_share: 0.065,
  driver_credit: 0.085,
};

/**
 * Progressive income tax on a full-year gross amount.
 * IRD rounds taxable income DOWN to the nearest whole dollar before
 * applying the brackets, so we mirror that here.
 */
export function incomeTax(annualIncome) {
  const gross = Math.max(0, Math.floor(Number(annualIncome) || 0));
  let remaining = gross;
  let prev = 0;
  let tax = 0;
  for (const { cap, rate } of TAX_BRACKETS) {
    const band = Math.min(remaining, cap - prev);
    if (band <= 0) break;
    tax += band * rate;
    remaining -= band;
    prev = cap;
    if (remaining <= 0) break;
  }
  return Math.round(tax * 100) / 100;
}

/**
 * ACC earner's levy on a full-year gross, capped.
 */
export function accLevy(annualIncome) {
  const gross = Math.max(0, Number(annualIncome) || 0);
  return Math.round(Math.min(gross, ACC_EARNER_LEVY.cap) * ACC_EARNER_LEVY.rate * 100) / 100;
}

/**
 * Tax and ACC attributable to an ADDITIONAL slice of income stacked on
 * top of existing main-job earnings. Uses the difference-of-totals method
 * so the result reflects the driver's true marginal cost, including any
 * bracket transitions the extra income triggers.
 *
 * @returns { tax, acc, total, marginalRatePct }
 */
export function marginalTaxOn(extraIncome, mainIncome) {
  const main = Math.max(0, Number(mainIncome) || 0);
  const extra = Math.max(0, Number(extraIncome) || 0);
  const totalTax = incomeTax(main + extra);
  const baseTax = incomeTax(main);
  const totalAcc = accLevy(main + extra);
  const baseAcc = accLevy(main);
  const tax = Math.max(0, totalTax - baseTax);
  const acc = Math.max(0, totalAcc - baseAcc);
  const marginalRatePct = extra > 0 ? ((tax + acc) / extra) * 100 : 0;
  return {
    tax: Math.round(tax * 100) / 100,
    acc: Math.round(acc * 100) / 100,
    total: Math.round((tax + acc) * 100) / 100,
    marginalRatePct: Math.round(marginalRatePct * 100) / 100,
  };
}

/**
 * IRD kilometre-rate vehicle deduction.
 * Tier 1 applies to the business share of the first 14,000 total km.
 * Tier 2 applies to all remaining business km.
 *
 * @param businessKm  km driven for the income-earning activity
 * @param totalKm     total km driven that year (business + private).
 *                    If unknown, pass businessKm and we'll assume 100%
 *                    business (the tier split then depends only on
 *                    whether businessKm exceeds 14,000).
 * @param fuelType    key of IRD_KM_RATES
 */
export function irdKmDeduction(businessKm, totalKm, fuelType) {
  const bkm = Math.max(0, Number(businessKm) || 0);
  const tkm = Math.max(bkm, Number(totalKm) || bkm);
  const rates = IRD_KM_RATES[fuelType] || IRD_KM_RATES.petrol;

  // Business fraction of total vehicle use
  const businessFraction = tkm > 0 ? bkm / tkm : 1;
  // Tier 1 slice = first 14,000 km of TOTAL travel × business fraction
  const tier1BusinessKm = Math.min(bkm, IRD_KM_TIER_THRESHOLD * businessFraction);
  const tier2BusinessKm = Math.max(0, bkm - tier1BusinessKm);

  const tier1Amount = tier1BusinessKm * rates.tier1;
  const tier2Amount = tier2BusinessKm * rates.tier2;

  return {
    tier1Km: tier1BusinessKm,
    tier2Km: tier2BusinessKm,
    tier1Rate: rates.tier1,
    tier2Rate: rates.tier2,
    tier1Amount,
    tier2Amount,
    total: Math.round((tier1Amount + tier2Amount) * 100) / 100,
  };
}
